import { Typography } from '@arionbanki/arionui';
import { Link } from 'react-router';
import type { CompletionMetricsSummary, CompletionWindowType } from 'types';
import { COMPLETION_ENDPOINT_LABEL, COMPLETION_WINDOWS } from './CompletionMetrics.consts';
import {
	createFormatters,
	dropOffPercent,
	formatUtcDate,
	formatUtcDateTime,
	type NumberSeparators
} from './CompletionMetrics.helpers';
import { ActivePopulationCard, ChannelCard, OutcomeCard, TrafficCard } from './CompletionMetricsSections';
import { useCompletionMetrics } from './useCompletionMetrics';

export function CompletionMetrics() {
	const { error, isLoading, monthlyTraffic, separators, setWindowType, summary, t, windowType } = useCompletionMetrics();

	return (
		<div className="admin-dashboard admin-metrics">
			<MetricsHeader summary={summary} t={t} />
			<WindowSelector setWindowType={setWindowType} t={t} windowType={windowType} />
			<div className="admin-dashboard__messages">
				{isLoading && (
					<div aria-live="polite" className="admin-loading" role="status">
						<span className="admin-loading__spinner" />
						<Typography TagName="p">{t('loading')}</Typography>
					</div>
				)}
				{error && <Typography TagName="p">{t('error')}</Typography>}
			</div>
			{summary && (
				<>
					<MetricsKpis separators={separators} summary={summary} t={t} />
					<div className="admin-metrics__grid">
						<OutcomeCard separators={separators} summary={summary} t={t} />
						<ActivePopulationCard separators={separators} summary={summary} t={t} />
					</div>
					<ChannelCard separators={separators} summary={summary} t={t} />
					<TrafficCard rows={monthlyTraffic} separators={separators} t={t} />
				</>
			)}
		</div>
	);
}

type HeaderProps = {
	summary: CompletionMetricsSummary | undefined;
	t: (key: string) => string;
};

function MetricsHeader({ summary, t }: Readonly<HeaderProps>) {
	return (
		<div className="admin-dashboard__header">
			<div>
				<Typography TagName="h3">{t('admin_metrics_title')}</Typography>
				<div className="admin-metrics__subtitle">
					<code className="admin-metrics__endpoint">{COMPLETION_ENDPOINT_LABEL}</code>
					{summary && (
						<span className="admin-metrics__range">
							{formatUtcDate(summary.windowStartUtc)} → {formatUtcDate(summary.windowEndUtc)}
						</span>
					)}
				</div>
			</div>
			<div className="admin-metrics__header-side">
				<Link className="admin-prefs-btn" to="/admin">
					← {t('admin_metrics_back')}
				</Link>
				<div className="admin-metrics__calculated">
					<span className="admin-metrics__calculated-label">{t('calculated_at')}</span>
					<span className="admin-metrics__calculated-value">
						{summary ? formatUtcDateTime(summary.calculatedAtUtc) : '—'}
					</span>
				</div>
			</div>
		</div>
	);
}

type WindowSelectorProps = {
	setWindowType: (windowType: CompletionWindowType) => void;
	windowType: CompletionWindowType;
	t: (key: string) => string;
};

function WindowSelector({ setWindowType, t, windowType }: Readonly<WindowSelectorProps>) {
	return (
		<div className="admin-dashboard__tabs" role="tablist">
			{COMPLETION_WINDOWS.map(window => (
				<button
					aria-selected={window.key === windowType}
					className={`admin-tab${window.key === windowType ? ' admin-tab--active' : ''}`}
					key={window.key}
					onClick={() => setWindowType(window.key)}
					role="tab"
					type="button"
				>
					{t(window.label)}
				</button>
			))}
		</div>
	);
}

function MetricsKpis({
	separators,
	summary,
	t
}: Readonly<{ separators: NumberSeparators; summary: CompletionMetricsSummary, t: (key: string) => string; }>) {
	const { formatCount, formatDecimal } = createFormatters(separators);
	const cards = [
		{
			key: 'completion',
			className: ' admin-metrics-kpi--primary',
			label: 'completion_rate',
			value: formatDecimal(summary.completedPercent),
			unit: '%',
			sub: t('processes_reached_status_').replace('{completed}', formatCount(summary.completedCount)).replace('{total}', formatCount(summary.totalProcesses))
		},
		{
			key: 'active',
			className: '',
			label: 'excl_canceled_expired',
			value: formatDecimal(summary.completedPercentExcludingCanceledExpired),
			unit: '%',
			sub: t('completed_of_active_processes').replace('{completed}', formatCount(summary.completedCount)).replace('{active}', formatCount(summary.activePopulationExcludingCanceledExpiredCount))
		},
		{
			key: 'created',
			className: '',
			label: 'processes_created',
			value: formatCount(summary.totalProcesses),
			unit: '',
			sub: t('created_in_the_selected_window')
		},
		{
			key: 'dropoff',
			className: 'admin-metrics-kpi--danger',
			label: 'dropped_off',
			value: formatDecimal(dropOffPercent(summary)),
			unit: '%',
			sub: t('canceled_expired').replace('{canceled}', formatCount(summary.canceled50Count)).replace('{expired}', formatCount(summary.expired60Count))
		}
	];

	return (
		<div className="admin-metrics__kpis">
			{cards.map(card => (
				<div className={`admin-metrics-kpi${card.className}`} key={card.key}>
					<div className="admin-metrics-kpi__label">{t(card.label)}</div>
					<div className="admin-metrics-kpi__figure">
						<span className="admin-metrics-kpi__value">{card.value}</span>
						{card.unit && <span className="admin-metrics-kpi__unit">{card.unit}</span>}
					</div>
					<div className="admin-metrics-kpi__sub">{card.sub}</div>
				</div>
			))}
		</div>
	);
}
